import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Save, ArrowLeft, X, Star, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [formData, setFormData] = useState({
    author: '',
    text: '',
    rating: 5,
    location: ''
  });
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/testimonials/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast({
        title: "Fehler",
        description: "Bewertungen konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      author: testimonial.author,
      text: testimonial.text,
      rating: testimonial.rating,
      location: testimonial.location || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setAddingNew(false);
    setFormData({ author: '', text: '', rating: 5, location: '' });
  };

  const handleSave = async () => {
    if (!formData.author.trim() || !formData.text.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte fuellen Sie alle Pflichtfelder aus",
        variant: "destructive"
      });
      return;
    }

    const token = localStorage.getItem('authToken');
    
    try {
      if (editingId) {
        await axios.put(
          `${API}/testimonials/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Gespeichert!",
          description: "Bewertung wurde aktualisiert"
        });
      } else {
        await axios.post(
          `${API}/testimonials`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({
          title: "Erstellt!",
          description: "Neue Bewertung wurde erstellt"
        });
      }
      
      handleCancelEdit();
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Bewertung konnte nicht gespeichert werden",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (testimonialId, currentStatus) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${API}/testimonials/${testimonialId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: currentStatus ? "Ausgeblendet" : "Sichtbar",
        description: currentStatus 
          ? "Bewertung wird nicht mehr angezeigt" 
          : "Bewertung wird jetzt angezeigt"
      });
      
      fetchTestimonials();
    } catch (error) {
      console.error('Error toggling testimonial:', error);
      toast({
        title: "Fehler",
        description: "Status konnte nicht geaendert werden",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (testimonialId, authorName) => {
    if (!window.confirm(`Moechten Sie die Bewertung von "${authorName}" wirklich loeschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API}/testimonials/${testimonialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Geloescht",
        description: "Bewertung wurde erfolgreich geloescht"
      });
      
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Bewertung konnte nicht geloescht werden",
        variant: "destructive"
      });
    }
  };

  const renderStars = (rating, interactive = false) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={interactive ? 24 : 16}
            className={`${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={interactive ? () => setFormData({ ...formData, rating: star }) : undefined}
          />
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Wird geladen...</p>
      </div>
    );
  }

  const renderForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Name des Kunden *</label>
        <input
          type="text"
          value={formData.author}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
          placeholder="z.B. Maria Mueller"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ort (optional)</label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
          placeholder="z.B. Berlin"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Bewertung</label>
        {renderStars(formData.rating, true)}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Bewertungstext *</label>
        <textarea
          value={formData.text}
          onChange={(e) => setFormData({ ...formData, text: e.target.value })}
          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none resize-none"
          rows="4"
          placeholder="Was hat der Kunde gesagt..."
        />
      </div>
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Save size={18} />
          {editingId ? 'Speichern' : 'Erstellen'}
        </button>
        <button
          onClick={handleCancelEdit}
          className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/settings')}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Zurueck zu Einstellungen
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Kundenbewertungen</h1>
              <p className="text-gray-600 mt-2">Verwalten Sie Bewertungen fuer die Startseite</p>
            </div>
            <button
              onClick={() => setAddingNew(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus size={20} />
              Neue Bewertung
            </button>
          </div>
        </div>

        {addingNew && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Neue Bewertung erstellen</h2>
            {renderForm()}
          </div>
        )}

        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <div 
              key={testimonial.id} 
              className={`bg-white rounded-lg shadow-md p-6 ${!testimonial.is_active ? 'opacity-60' : ''}`}
            >
              {editingId === testimonial.id ? (
                renderForm()
              ) : (
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold">{testimonial.author}</h3>
                      {testimonial.location && (
                        <span className="text-sm text-gray-500">aus {testimonial.location}</span>
                      )}
                      {!testimonial.is_active && (
                        <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                          Ausgeblendet
                        </span>
                      )}
                    </div>
                    <div className="mb-3">
                      {renderStars(testimonial.rating)}
                    </div>
                    <p className="text-gray-600 italic">"{testimonial.text}"</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(testimonial.id, testimonial.is_active)}
                      className={`p-2 rounded-lg transition-all ${
                        testimonial.is_active 
                          ? 'text-gray-600 hover:text-orange-600 hover:bg-orange-50' 
                          : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title={testimonial.is_active ? 'Ausblenden' : 'Anzeigen'}
                    >
                      {testimonial.is_active ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                    <button
                      onClick={() => handleEdit(testimonial)}
                      className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-all"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(testimonial.id, testimonial.author)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {testimonials.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 mb-4">Noch keine Bewertungen vorhanden</p>
            <button
              onClick={() => setAddingNew(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus size={20} />
              Erste Bewertung erstellen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
